import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { EntityFormDialog, ConfirmDialog } from '@/components/shared';
import type { EventItem, EventFormData, Organizer, Series } from './eventsmanager.props';

interface EventsFormDialogProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  editingEvent: EventItem | null;
  setEditingEvent: (e: EventItem | null) => void;
  formData: EventFormData;
  setFormData: (data: EventFormData) => void;
  handleSubmit: (e: React.FormEvent) => void;
  deleteTarget: any;
  setDeleteTarget: (target: any) => void;
  confirmDelete: () => void;
  organizers: Organizer[];
  allSeries: Series[];
  isOrganizer: () => boolean;
  user: any;
}

const EventsFormDialog: React.FC<EventsFormDialogProps> = ({
  isDialogOpen, setIsDialogOpen, editingEvent, setEditingEvent,
  formData, setFormData, handleSubmit, deleteTarget, setDeleteTarget, confirmDelete,
  organizers, allSeries, isOrganizer, user,
}) => (
  <>
    <EntityFormDialog
      open={isDialogOpen}
      onOpenChange={setIsDialogOpen}
      title={editingEvent ? 'Измени настан' : 'Додај нов настан'}
      trigger={
        <Button onClick={() => {
          setEditingEvent(null);
          if (isOrganizer() && user?.organizerId) {
            setFormData({ ...formData, organizerId: user.organizerId });
          }
        }}>
          <Plus className="w-4 h-4 mr-2" />
          Додај настан
        </Button>
      }
      onSubmit={handleSubmit}
      submitLabel={editingEvent ? 'Зачувај промени' : 'Креирај настан'}
    >
      <div>
        <Label htmlFor="title">Наслов *</Label>
        <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
      </div>
      <div>
        <Label htmlFor="subtitle">Поднаслов</Label>
        <Input id="subtitle" value={formData.subtitle} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="location">Локација *</Label>
          <Input id="location" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required />
        </div>
        <div>
          <Label htmlFor="organizerId">Организатор *</Label>
          <select
            id="organizerId"
            value={formData.organizerId}
            onChange={(e) => setFormData({ ...formData, organizerId: e.target.value })}
            required
            disabled={isOrganizer()}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">-- Изберете организатор --</option>
            {organizers.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <Label htmlFor="raceSeriesId">Серија (опционално)</Label>
        <select
          id="raceSeriesId"
          value={formData.raceSeriesId}
          onChange={(e) => setFormData({ ...formData, raceSeriesId: e.target.value })}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">-- Без серија --</option>
          {allSeries.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">Групирајте го настанот со други едиции во серија</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="date">Датум *</Label>
          <Input id="date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
        </div>
        <div>
          <Label htmlFor="time">Време * (UTC)</Label>
          <Input id="time" type="time" value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })} required />
          <p className="text-xs text-gray-500 mt-1">Внесете време во UTC временска зона</p>
        </div>
      </div>
      <div>
        <Label htmlFor="image">URL на слика *</Label>
        <Input id="image" value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} placeholder="https://example.com/image.jpg" required />
      </div>
      <div>
        <Label htmlFor="description">Опис</Label>
        <Input id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Краток опис на настанот" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="openRegistrationDate">Датум на отворање на регистрација</Label>
          <Input id="openRegistrationDate" type="date" value={formData.openRegistrationDate} onChange={(e) => setFormData({ ...formData, openRegistrationDate: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="closeRegistrationDate">Датум на затворање на регистрација</Label>
          <Input id="closeRegistrationDate" type="date" value={formData.closeRegistrationDate} onChange={(e) => setFormData({ ...formData, closeRegistrationDate: e.target.value })} />
        </div>
      </div>
      <div>
        <Label htmlFor="status">Статус *</Label>
        <select
          id="status"
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        >
          <option value="upcoming">Следен настан</option>
          <option value="past">Поминат настан</option>
        </select>
      </div>
    </EntityFormDialog>
    <ConfirmDialog
      open={deleteTarget !== null}
      onOpenChange={(open) => !open && setDeleteTarget(null)}
      title="Избриши настан"
      description="Дали сте сигурни дека сакате да го избришете овој настан?"
      onConfirm={confirmDelete}
    />
  </>
);

export default EventsFormDialog;
